/**
 * Read a clip's embedded start timecode (the QuickTime / MP4 `tmcd`
 * track) in the browser, before it uploads.
 *
 * Why: PictureUp's exports have to write source timecodes in the clip's
 * own timecode space or the NLE conforms the cut to the wrong place. Mux
 * doesn't report timecode, so the export used to assume 00:00:00:00 for
 * every customer upload. Resolve stamps 01:00:00:00 on exports by
 * default, so a customer's interview exported from Resolve got an EDL an
 * hour off: Resolve linked the clips to other media or left them offline
 * (found on the Deloitte job, 2026-10-18).
 *
 * Reads only box headers plus the `moov` box (wherever it sits, start or
 * end of the file) and 4 bytes of the timecode sample, never the media.
 * Returns null when there is no timecode track or anything is off; the
 * caller then keeps the old default.
 */

export type ReadRange = (offset: number, length: number) => Promise<Uint8Array>;

const MAX_MOOV = 64 * 1024 * 1024;

function u32(b: Uint8Array, o: number): number {
  return ((b[o]! << 24) | (b[o + 1]! << 16) | (b[o + 2]! << 8) | b[o + 3]!) >>> 0;
}
function u64(b: Uint8Array, o: number): number {
  return u32(b, o) * 2 ** 32 + u32(b, o + 4);
}
function type4(b: Uint8Array, o: number): string {
  return String.fromCharCode(b[o]!, b[o + 1]!, b[o + 2]!, b[o + 3]!);
}

type Box = { type: string; start: number; header: number; size: number };

/** Child boxes of the byte range [from, to) of `b`. */
function children(b: Uint8Array, from: number, to: number): Box[] {
  const out: Box[] = [];
  let o = from;
  while (o + 8 <= to) {
    let size = u32(b, o);
    const type = type4(b, o + 4);
    let header = 8;
    if (size === 1) {
      if (o + 16 > to) break;
      size = u64(b, o + 8);
      header = 16;
    } else if (size === 0) {
      size = to - o;
    }
    if (size < header || o + size > to) break;
    out.push({ type, start: o, header, size });
    o += size;
  }
  return out;
}

function child(b: Uint8Array, parent: Box, type: string): Box | undefined {
  return children(b, parent.start + parent.header, parent.start + parent.size).find((c) => c.type === type);
}

/** Find the top-level `moov` box by walking box headers through the file. */
async function findMoov(read: ReadRange, fileSize: number): Promise<{ offset: number; size: number } | null> {
  let o = 0;
  for (let guard = 0; guard < 4096 && o + 8 <= fileSize; guard++) {
    const h = await read(o, Math.min(16, fileSize - o));
    if (h.length < 8) return null;
    let size = u32(h, 0);
    const type = type4(h, 4);
    if (size === 1) {
      if (h.length < 16) return null;
      size = u64(h, 8);
    } else if (size === 0) {
      size = fileSize - o;
    }
    if (size < 8) return null;
    if (type === 'moov') return { offset: o, size };
    o += size;
  }
  return null;
}

/** "HH:MM:SS:FF" (non-drop labels) from a frame count at an integer rate. */
export function framesToTc(frames: number, fps: number): string {
  const p = (n: number) => String(n).padStart(2, '0');
  const ff = frames % fps;
  const total = Math.floor(frames / fps);
  return `${p(Math.floor(total / 3600) % 24)}:${p(Math.floor(total / 60) % 60)}:${p(total % 60)}:${p(ff)}`;
}

export async function readStartTimecode(read: ReadRange, fileSize: number): Promise<string | null> {
  const moov = await findMoov(read, fileSize);
  if (!moov || moov.size > MAX_MOOV) return null;
  const b = await read(moov.offset, moov.size);
  for (const trak of children(b, 8, b.length).filter((c) => c.type === 'trak')) {
    const mdia = child(b, trak, 'mdia');
    const hdlr = mdia && child(b, mdia, 'hdlr');
    // hdlr: version/flags(4) pre_defined(4) handler_type(4)
    if (!hdlr || type4(b, hdlr.start + hdlr.header + 8) !== 'tmcd') continue;
    const stbl = (() => {
      const minf = child(b, mdia!, 'minf');
      return minf && child(b, minf, 'stbl');
    })();
    if (!stbl) continue;
    const stsd = child(b, stbl, 'stsd');
    // stsd: version/flags(4) entry_count(4), then the tmcd sample entry:
    // size(4) 'tmcd'(4) reserved(6) data_ref(2) reserved(4) flags(4)
    // timescale(4) frameDuration(4) numberOfFrames(1)
    if (!stsd) continue;
    const e = stsd.start + stsd.header + 8;
    if (type4(b, e + 4) !== 'tmcd') continue;
    const timescale = u32(b, e + 24);
    const frameDuration = u32(b, e + 28);
    const nFrames = b[e + 32]!;
    const fps = nFrames || (frameDuration ? Math.round(timescale / frameDuration) : 0);
    if (!fps) continue;
    const stco = child(b, stbl, 'stco');
    const co64 = child(b, stbl, 'co64');
    let chunk: number | null = null;
    if (stco && u32(b, stco.start + stco.header + 4) > 0) chunk = u32(b, stco.start + stco.header + 8);
    else if (co64 && u32(b, co64.start + co64.header + 4) > 0) chunk = u64(b, co64.start + co64.header + 8);
    if (chunk == null || chunk + 4 > fileSize) continue;
    const sample = await read(chunk, 4);
    if (sample.length < 4) continue;
    return framesToTc(u32(sample, 0), fps);
  }
  return null;
}

/** Browser convenience: read straight from a File. Never throws. */
export async function readFileStartTimecode(file: File): Promise<string | null> {
  try {
    const read: ReadRange = async (offset, length) =>
      new Uint8Array(await file.slice(offset, offset + length).arrayBuffer());
    return await readStartTimecode(read, file.size);
  } catch {
    return null;
  }
}
